import React from 'react';
import type { LandingVisualPhase } from '../../hooks/useInViewSection';
import './ScrollHeroVisual.css';

interface Props {
  phase: LandingVisualPhase;
  scrollProgress: number;
}

const ScrollHeroVisual: React.FC<Props> = ({ phase, scrollProgress }) => {
  const parallaxY = scrollProgress * 40;

  return (
    <div
      className="scroll-hero-visual"
      style={{ '--parallax-y': `${parallaxY}px` } as React.CSSProperties}
    >
      <div className="scroll-hero-visual__glow" aria-hidden />
      <div className="scroll-hero-visual__ring scroll-hero-visual__ring--1" aria-hidden />
      <div className="scroll-hero-visual__ring scroll-hero-visual__ring--2" aria-hidden />
      <div className="scroll-hero-visual__ring scroll-hero-visual__ring--3" aria-hidden />

      <div className={`scroll-hero-visual__stage scroll-hero-visual__stage--${phase}`}>
        <svg
          className="scroll-hero-visual__graphic scroll-hero-visual__graphic--hero"
          viewBox="0 0 320 320"
          fill="none"
          aria-hidden
        >
          <circle cx="160" cy="160" r="120" stroke="currentColor" strokeWidth="2" opacity="0.25" />
          <circle cx="160" cy="160" r="88" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
          <circle cx="160" cy="160" r="24" fill="currentColor" opacity="0.9" />
          {[0, 45, 90, 135].map((deg) => (
            <line
              key={deg}
              x1="160"
              y1="160"
              x2={160 + 100 * Math.cos((deg * Math.PI) / 180)}
              y2={160 + 100 * Math.sin((deg * Math.PI) / 180)}
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.35"
            />
          ))}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <rect
              key={i}
              x={48 + i * 28}
              y={200 + (i % 2) * 8}
              width="12"
              height={32 + (i % 3) * 16}
              rx="4"
              className="scroll-hero-visual__bar"
              style={{ animationDelay: `${i * 0.08}s` }}
            />
          ))}
        </svg>

        <svg
          className="scroll-hero-visual__graphic scroll-hero-visual__graphic--rapper"
          viewBox="0 0 320 320"
          fill="none"
          aria-hidden
        >
          <rect x="130" y="60" width="60" height="100" rx="30" stroke="currentColor" strokeWidth="3" />
          <path d="M160 160 v80" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M120 240 h80" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <ellipse cx="160" cy="72" rx="36" ry="20" stroke="currentColor" strokeWidth="2" opacity="0.5" />
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={i}
              d={`M ${200 + i * 12} 100 Q ${220 + i * 14} 120 ${200 + i * 12} 140`}
              stroke="currentColor"
              strokeWidth="2"
              opacity="0.6"
              className="scroll-hero-visual__pulse-line"
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </svg>

        <svg
          className="scroll-hero-visual__graphic scroll-hero-visual__graphic--producer"
          viewBox="0 0 320 320"
          fill="none"
          aria-hidden
        >
          <rect x="40" y="100" width="240" height="120" rx="12" stroke="currentColor" strokeWidth="2" opacity="0.5" />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <g key={i}>
              <rect x={56 + i * 36} y="130" width="20" height="60" rx="4" fill="currentColor" opacity="0.15" />
              <circle
                cx={66 + i * 36}
                cy="118"
                r="8"
                fill="currentColor"
                className="scroll-hero-visual__knob"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            </g>
          ))}
          <path d="M 60 240 Q 160 200 260 240" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.7" />
        </svg>

        <svg
          className="scroll-hero-visual__graphic scroll-hero-visual__graphic--listener"
          viewBox="0 0 320 320"
          fill="none"
          aria-hidden
        >
          <path
            d="M 80 180 Q 80 80 160 80 Q 240 80 240 180"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
          />
          <rect x="56" y="160" width="48" height="72" rx="16" stroke="currentColor" strokeWidth="3" />
          <rect x="216" y="160" width="48" height="72" rx="16" stroke="currentColor" strokeWidth="3" />
          <path d="M 160 200 v24" stroke="currentColor" strokeWidth="2" className="scroll-hero-visual__heart-line" />
          <circle cx="160" cy="232" r="12" fill="currentColor" opacity="0.85" className="scroll-hero-visual__heart" />
        </svg>

        <svg
          className="scroll-hero-visual__graphic scroll-hero-visual__graphic--cta"
          viewBox="0 0 320 320"
          fill="none"
          aria-hidden
        >
          {[
            [160, 80],
            [80, 200],
            [240, 200],
            [160, 260],
          ].map(([cx, cy], i) => (
            <g key={i}>
              <circle cx={cx} cy={cy} r="28" stroke="currentColor" strokeWidth="2" />
              <circle cx={cx} cy={cy} r="10" fill="currentColor" />
            </g>
          ))}
          <line x1="160" y1="108" x2="100" y2="178" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
          <line x1="160" y1="108" x2="220" y2="178" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
          <line x1="100" y1="222" x2="160" y2="232" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
          <line x1="220" y1="222" x2="160" y2="232" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
        </svg>
      </div>
    </div>
  );
};

export default ScrollHeroVisual;
